/**
 * Variant picker additions for the product page.
 *
 * <variant-selection>  Keeps the "nothing selected yet" state honest and stops the product form
 *                      and the dynamic checkout button from submitting without a choice.
 * <notify-me>          Back in stock subscription modal. Posts straight to the Klaviyo client API,
 *                      the same endpoint the previous theme used.
 */

const KLAVIYO_ENDPOINT = 'https://a.klaviyo.com/client/back-in-stock-subscriptions/';
const KLAVIYO_REVISION = '2024-02-15';

/**
 * Prestige animates its dialogs differently either side of a breakpoint — the wide transition
 * fades the element itself, the narrow one animates only the parts inside the shadow root — and
 * neither clears what the other left inline. Cross the breakpoint between two opens and the
 * leftovers bite: `opacity: 0` on the element makes the dialog open fully transparent (it still
 * takes clicks, so it reads as "nothing happens"), and `transform: translateY(100%)` on the
 * content part pushes it down by its own height, leaving an empty box behind. The breakpoint is
 * 1000px for a popover and 700px for a modal, so both are affected.
 *
 * Clearing the leftovers before every show costs nothing: each animation sets what it needs.
 */
function clearDialogAnimationLeftovers(dialog) {
  dialog.style.removeProperty('opacity');

  dialog.shadowRoot?.querySelectorAll('[part~="content"], [part~="overlay"]').forEach((part) => {
    part.style.removeProperty('transform');
    part.style.removeProperty('opacity');
  });
}

if (!customElements.get('variant-selection')) {
  customElements.define(
    'variant-selection',
    class VariantSelection extends HTMLElement {
      connectedCallback() {
        this.picker = this.closest('variant-picker');

        if (!this.picker) return;

        // Liquid cannot tell an initial page load apart from the Section Rendering request Prestige
        // fires when an option changes, because neither carries a ?variant= parameter. It therefore
        // always renders the unselected state, and we clear it here when the browser URL says a
        // variant is in fact selected. Prestige writes that parameter before it re-renders, and this
        // runs synchronously as the new markup is inserted, so nothing flashes.
        if (new URL(window.location.href).searchParams.has('variant')) {
          this.picker.removeAttribute('data-unselected');
        }

        this.abortController = new AbortController();
        const { signal } = this.abortController;

        // Both listeners sit on the document in the capture phase, which always runs before
        // anything bound to the form itself. <product-form> binds submit to the form, and the
        // dynamic checkout button bypasses the form altogether.
        document.addEventListener('submit', this.onSubmit.bind(this), { capture: true, signal });
        document.addEventListener('click', this.onDocumentClick.bind(this), { capture: true, signal });

        // The option radios carry a form attribute but live outside the form element, so their
        // change events never reach it. Listen on the picker instead.
        this.picker.addEventListener('change', this.clearErrors.bind(this), { signal });

        this.watchPopovers();

        // Crossing the breakpoint with the dropdown open leaves the theme's scroll lock behind,
        // because it only releases the lock while the media query still matches. Close instead.
        window
          .matchMedia('(min-width: 1000px)')
          .addEventListener('change', this.closeDropdowns.bind(this), { signal });
      }

      /**
       * Below 1000px Prestige moves an opening popover into document.body and puts it back only
       * when it closes, using a reference it captures at open time (Popover.shouldAppendToBody).
       * That reference is cleared on every open, so a popover opened again while still in the
       * body, or one whose section re-rendered underneath it, never finds its way back: the
       * picker is left with an empty gap and the dropdown turns up as a stray box further down
       * the page. Leaving the move alone and escorting the element home afterwards is safer than
       * suppressing it — the bottom sheet relies on being a child of the body to paint above the
       * rest of the page.
       */
      watchPopovers() {
        document.querySelectorAll('.variant-picker__popover').forEach((popover) => {
          if (popover.dataset.homeWatch !== 'true') {
            popover.dataset.homeWatch = 'true';
            popover.addEventListener('dialog:after-hide', () => this.sendPopoverHome(popover));

            popover.addEventListener('dialog:before-show', () => clearDialogAnimationLeftovers(popover));
          }

          if (popover.parentElement !== document.body) return;

          if (popover.hasAttribute('open')) {
            popover.hide();
          } else {
            this.sendPopoverHome(popover);
          }
        });
      }

      sendPopoverHome(popover) {
        if (popover.parentElement !== document.body) return;

        // The trigger button's parent is the wrapper the popover is rendered in.
        const home = document.querySelector(`[aria-controls="${CSS.escape(popover.id)}"]`)?.parentElement;

        // No trigger, or the markup was re-rendered and already holds a replacement: this one is
        // a leftover and would otherwise duplicate an id.
        if (!home || home.querySelector(`:scope > #${CSS.escape(popover.id)}`)) {
          popover.remove();
          return;
        }

        home.appendChild(popover);
      }

      closeDropdowns() {
        document.querySelectorAll('.variant-picker__popover[open]').forEach((popover) => {
          if (typeof popover.hide !== 'function') return;

          // The theme skips its scroll-lock bookkeeping when the breakpoint no longer matches,
          // so the lock class can outlive the popover. Clear it once nothing is open.
          popover.addEventListener(
            'dialog:after-hide',
            () => {
              if (document.querySelector('x-popover[open], x-modal[open], x-drawer[open]')) return;

              document.documentElement.classList.remove('lock');
            },
            { once: true }
          );

          popover.hide();
        });
      }

      disconnectedCallback() {
        this.abortController?.abort();
      }

      get isUnselected() {
        return this.picker.hasAttribute('data-unselected');
      }

      /**
       * The form is matched by its id attribute rather than looked up through document.forms. The
       * picker is rendered before the buy buttons, and a lookup that comes back empty would leave
       * the guard silently unbound.
       */
      get formId() {
        return this.getAttribute('form-id');
      }

      onSubmit(event) {
        if (event.target.getAttribute('id') !== this.formId) return;
        if (!this.isUnselected) return;
        if (!this.reportMissingSelection()) return;

        event.preventDefault();
        event.stopImmediatePropagation();
      }

      /**
       * Two things are blocked here. The dynamic checkout button never goes through the form at
       * all, and the add to cart button is caught at click time as well as on submit, so the guard
       * does not depend on the submit event reaching the document.
       */
      onDocumentClick(event) {
        if (!this.isUnselected) return;

        const form = event.target.closest(`#${CSS.escape(this.formId)}`);

        if (!form) return;

        const isCheckoutButton = Boolean(event.target.closest('.shopify-payment-button'));
        const isSubmitButton = Boolean(event.target.closest('[type="submit"]'));

        if (!isCheckoutButton && !isSubmitButton) return;
        if (!this.reportMissingSelection()) return;

        event.preventDefault();
        event.stopImmediatePropagation();
      }

      /**
       * Points the customer at the first option they still have to choose and reports whether
       * there was one. Returning false when everything is already chosen is what keeps this from
       * killing a working form: the unselected flag is a server-side hint, and a hint must never
       * be able to block a submit that has nothing missing.
       */
      reportMissingSelection() {
        const option = Array.from(this.picker.querySelectorAll('.variant-picker__option')).find(
          (element) => !element.querySelector('input[data-option-position]:checked')
        );

        if (!option) return false;

        const error = option.querySelector('.variant-picker__error');
        const trigger = option.querySelector('button.select[aria-controls]');

        if (error) error.hidden = false;

        if (trigger) {
          trigger.focus();
          if (trigger.getAttribute('aria-expanded') === 'false') trigger.click();
        }

        return true;
      }

      clearErrors() {
        this.picker
          .querySelectorAll('.variant-picker__error:not([hidden])')
          .forEach((error) => (error.hidden = true));
      }
    }
  );
}

if (!customElements.get('notify-me')) {
  customElements.define(
    'notify-me',
    class NotifyMe extends HTMLElement {
      connectedCallback() {
        this.variants = this.parseVariants();
        this.form = this.querySelector('[data-notify-form]');
        this.titleElement = this.querySelector('[data-notify-variant-title]');
        this.dateElement = this.querySelector('[data-notify-date]');
        this.dateValueElement = this.querySelector('[data-notify-date-value]');
        this.messageElement = this.querySelector('[data-notify-message]');
        this.selectToggle = this.querySelector('[data-notify-select-toggle]');
        this.selectList = this.querySelector('[data-notify-select-list]');
        this.modal = this.querySelector('x-modal');

        this.abortController = new AbortController();
        const { signal } = this.abortController;

        // The modal itself is opened by the theme through aria-controls; we only have to record
        // which variant the customer asked about.
        document.addEventListener('click', this.onNotifyClick.bind(this), { signal });
        this.form?.addEventListener('submit', this.onSubmit.bind(this), { signal });

        this.selectToggle?.addEventListener('click', this.toggleSelect.bind(this), { signal });
        this.selectList?.addEventListener('click', this.onSelectChoice.bind(this), { signal });

        this.modal?.addEventListener(
          'dialog:before-show',
          () => clearDialogAnimationLeftovers(this.modal),
          { signal }
        );
      }

      disconnectedCallback() {
        this.abortController?.abort();
      }

      parseVariants() {
        try {
          return JSON.parse(this.querySelector('[data-notify-variants]')?.textContent || '{}');
        } catch {
          return {};
        }
      }

      get isNotRestocking() {
        return this.hasAttribute('not-restocking');
      }

      /**
       * Two ways in: a "Notify me" row in the size dropdown, which already knows its variant,
       * and the sold out button above add to cart, which does not. In the second case the first
       * sold out variant is preselected so the modal is never in a half-filled state.
       */
      onNotifyClick(event) {
        const trigger = event.target.closest(`[aria-controls="${CSS.escape(this.getAttribute('modal-id'))}"]`);

        if (!trigger) return;

        const variantId = trigger.getAttribute('data-notify-variant');

        if (variantId) {
          this.setVariant(variantId);

          // The row lives inside the option dropdown, which does not close on its own.
          const popover = trigger.closest('x-popover');

          if (typeof popover?.hide === 'function') popover.hide();
        } else if (!this.variantId) {
          this.setVariant(Object.keys(this.variants)[0]);
        }

        this.closeSelect();
      }

      toggleSelect() {
        if (this.selectToggle.getAttribute('aria-expanded') === 'true') {
          this.closeSelect();
        } else {
          this.selectToggle.setAttribute('aria-expanded', 'true');
          this.selectList.hidden = false;
        }
      }

      closeSelect() {
        this.selectToggle?.setAttribute('aria-expanded', 'false');

        if (this.selectList) this.selectList.hidden = true;
      }

      onSelectChoice(event) {
        const option = event.target.closest('[data-notify-choose]');

        if (!option) return;

        this.setVariant(option.getAttribute('data-notify-choose'));
        this.closeSelect();
        this.selectToggle.focus();
      }

      setVariant(variantId) {
        this.variantId = variantId;

        const variant = this.variants[variantId] || {};

        if (this.titleElement) this.titleElement.textContent = variant.title || '';

        this.selectList?.querySelectorAll('[data-notify-choose]').forEach((option) => {
          option.setAttribute('aria-selected', String(option.getAttribute('data-notify-choose') === variantId));
        });

        if (this.dateElement) {
          // The wording comes straight from the metafield, so it is written as text, never HTML.
          const hasDate = Boolean(variant.date);

          if (this.dateValueElement) this.dateValueElement.textContent = variant.date || '';

          this.dateElement.hidden = !hasDate;
        }

        this.setMessage('');
      }

      setMessage(text, state) {
        if (!this.messageElement) return;

        this.messageElement.textContent = text;
        this.messageElement.hidden = !text;
        this.messageElement.classList.toggle('notify-me__message--error', state === 'error');
        this.messageElement.classList.toggle('notify-me__message--success', state === 'success');
      }

      async onSubmit(event) {
        event.preventDefault();

        const companyId = this.getAttribute('company-id');
        const emailField = this.form.elements.email;

        if (!this.form.reportValidity()) return;
        if (!companyId || !this.variantId) {
          this.setMessage(this.dataset.errorMessage, 'error');
          return;
        }

        const submitButton = this.form.querySelector('[type="submit"]');
        submitButton?.setAttribute('aria-busy', 'true');

        try {
          const response = await fetch(`${KLAVIYO_ENDPOINT}?company_id=${encodeURIComponent(companyId)}`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              revision: KLAVIYO_REVISION,
            },
            signal: this.abortController.signal,
            body: JSON.stringify({
              data: {
                type: 'back-in-stock-subscription',
                attributes: {
                  profile: {
                    data: {
                      type: 'profile',
                      attributes: { email: emailField.value.trim() },
                    },
                  },
                  channels: ['EMAIL'],
                },
                relationships: {
                  variant: {
                    data: {
                      type: 'catalog-variant',
                      id: `$shopify:::$default:::${this.variantId}`,
                    },
                  },
                },
              },
            }),
          });

          if (response.status === 202) {
            this.setMessage(this.dataset.successMessage, 'success');
            this.form.reset();
          } else {
            this.setMessage(this.dataset.errorMessage, 'error');
          }
        } catch (error) {
          if (error.name !== 'AbortError') this.setMessage(this.dataset.errorMessage, 'error');
        } finally {
          submitButton?.removeAttribute('aria-busy');
        }
      }
    }
  );
}
